/**
 * Discovery View - Display patterns and targets discovered by discovery engine
 */

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Radar,
  Sparkles,
  Globe,
  Database,
  Play,
  Pause,
  RefreshCw,
  Check,
  ArrowRight,
  TrendingUp,
  Clock,
  BookOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { discoveryService } from "@/services/discoveryService";
import type { DiscoveredPatternLocal, DiscoveryTarget, DiscoveryStats } from "@/services/discoveryService";
import * as Dialog from "@radix-ui/react-dialog";
import * as Tabs from "@radix-ui/react-tabs";

export function DiscoveryView() {
  const [stats, setStats] = useState<DiscoveryStats>({
    patternsDiscovered: 0,
    targetsFound: 0,
    apisExplored: 0,
    lastDiscoveryRun: null,
    isRunning: false,
  });

  const [patterns, setPatterns] = useState<DiscoveredPatternLocal[]>([]);
  const [targets, setTargets] = useState<DiscoveryTarget[]>([]);
  const [filterSaved, setFilterSaved] = useState<"all" | "unsaved" | "saved">("all");

  useEffect(() => {
    const unsubscribe = discoveryService.onEvent((event) => {
      if (event.type === "exploration_complete" || event.type === "status_change") {
        refreshData();
      }
    });

    refreshData();

    return () => unsubscribe();
  }, []);

  const refreshData = () => {
    setStats(discoveryService.getStats());
    setPatterns(discoveryService.getDiscoveredPatterns());
    setTargets(discoveryService.getDiscoveredTargets());
  };

  const toggleDiscovery = async () => {
    if (stats.isRunning) {
      discoveryService.stop();
    } else {
      await discoveryService.start(30000);
    }
    refreshData();
  };

  const triggerDiscovery = async () => {
    await discoveryService.discoverNow();
  };

  const savePattern = async (patternId: string) => {
    await discoveryService.savePattern(patternId);
    refreshData();
  };

  const createOperator = async (targetId: string) => {
    await discoveryService.createOperatorFromTarget(targetId);
    refreshData();
  };

  const filteredPatterns = patterns.filter((p) => {
    if (filterSaved === "saved") return p.saved;
    if (filterSaved === "unsaved") return !p.saved;
    return true;
  });

  const unsavedTargets = targets.filter((t) => !t.explored);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Discovery Engine</h1>
          <p className="text-muted-foreground mt-1">
            Autonomous pattern extraction and API discovery
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={triggerDiscovery}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-transparent border border-[hsl(var(--border))] rounded-md hover:bg-[hsl(var(--muted))] transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Run Now
          </button>
          <button
            onClick={toggleDiscovery}
            className={cn(
              "flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors",
              stats.isRunning
                ? "bg-red-500 text-white hover:bg-red-600"
                : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:opacity-90"
            )}
          >
            {stats.isRunning ? (
              <>
                <Pause className="h-4 w-4" />
                Stop
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Start
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatsCard
          title="Running"
          value={stats.isRunning ? "Active" : "Idle"}
          icon={Play}
          color={stats.isRunning ? "text-green-500" : "text-gray-500"}
        />
        <StatsCard
          title="Patterns Discovered"
          value={stats.patternsDiscovered.toString()}
          icon={Sparkles}
          color="text-purple-500"
        />
        <StatsCard
          title="Targets Found"
          value={stats.targetsFound.toString()}
          icon={Globe}
          color="text-blue-500"
        />
        <StatsCard
          title="Operators Created"
          value={stats.apisExplored.toString()}
          icon={Database}
          color="text-cyan-500"
        />
      </div>

      {/* Content Tabs */}
      <Tabs.Root defaultValue="patterns" className="w-full">
        <Tabs.List className="inline-flex h-10 items-center justify-center rounded-md bg-[hsl(var(--muted))] p-1 w-full">
          <Tabs.Trigger
            value="patterns"
            className={cn(
              "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex-1",
              filterSaved === "saved" ? "data-[state=active]" : ""
            )}
            onClick={() => setFilterSaved("all")}
          >
            Patterns
          </Tabs.Trigger>
          <Tabs.Trigger
            value="targets"
            className={cn(
              "inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex-1"
            )}
          >
            Targets
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="patterns">
          <div className="flex items-center gap-2 mb-4">
            <span
              onClick={() => setFilterSaved("all")}
              className={cn(
                "px-2 py-1 text-xs rounded cursor-pointer",
                filterSaved === "all" ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] hover:bg-[hsl(var(--muted)/.8)]"
              )}
            >
              All
            </span>
            <span
              onClick={() => setFilterSaved("unsaved")}
              className={cn(
                "px-2 py-1 text-xs rounded cursor-pointer",
                filterSaved === "unsaved" ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] hover:bg-[hsl(var(--muted)/.8)]"
              )}
            >
              Unsaved
            </span>
            <span
              onClick={() => setFilterSaved("saved")}
              className={cn(
                "px-2 py-1 text-xs rounded cursor-pointer",
                filterSaved === "saved" ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] hover:bg-[hsl(var(--muted)/.8)]"
              )}
            >
              Saved
            </span>
          </div>

          <div className="h-[600px] overflow-y-auto pr-2 space-y-3">
            <AnimatePresence>
              {filteredPatterns.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Radar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No patterns discovered yet</p>
                  <p className="text-sm">
                    Interact with AGI to trigger pattern extraction
                  </p>
                </div>
              ) : (
                filteredPatterns.map((pattern) => (
                  <PatternCard
                    key={pattern.id}
                    pattern={pattern}
                    onSave={() => savePattern(pattern.id)}
                  />
                ))
              )}
            </AnimatePresence>
          </div>
        </Tabs.Content>

        <Tabs.Content value="targets">
          <div className="h-[600px] overflow-y-auto pr-2 space-y-3">
            <AnimatePresence>
              {unsavedTargets.length === 0 && targets.every(t => t.explored) ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No targets available</p>
                  <p className="text-sm">
                    Discovery engine will find new APIs and services
                  </p>
                </div>
              ) : (
                targets.map((target) => (
                  <TargetCard
                    key={target.id}
                    target={target}
                    onCreate={() => createOperator(target.id)}
                  />
                ))
              )}
            </AnimatePresence>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

// Stats Card Component
function StatsCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: string;
  icon: any;
  color: string;
}) {
  return (
    <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-card-foreground shadow-sm">
      <div className="p-6 pb-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-muted-foreground">
            {title}
          </div>
          <Icon className={`h-4 w-4 ${color}`} />
        </div>
      </div>
      <div className="p-6 pt-3">
        <div className="text-2xl font-bold">{value}</div>
      </div>
    </div>
  );
}

// Pattern Card Component
function PatternCard({
  pattern,
  onSave,
}: {
  pattern: DiscoveredPatternLocal;
  onSave: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-card-foreground shadow-sm">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-lg font-semibold">
                <Sparkles className="h-4 w-4 text-purple-500 flex-shrink-0" />
                <span className="truncate">{pattern.intent}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">
                Discovered via {pattern.source} • {pattern.confidence >= 0.7 ? "High" : "Medium"} confidence
              </div>
            </div>
            <span className={cn(
              "px-2 py-1 text-xs rounded",
              pattern.saved ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-muted-foreground"
            )}>
              {pattern.saved ? "Saved" : "Draft"}
            </span>
          </div>
        </div>

        <div className="p-6 pt-0 space-y-3">
          {pattern.triggerPhrases.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Trigger Phrases</div>
              <div className="flex flex-wrap gap-1">
                {pattern.triggerPhrases.map((phrase, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-[hsl(var(--muted))] rounded">
                    {phrase}
                  </span>
                ))}
              </div>
            </div>
          )}

          {pattern.suggestedOperators.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Suggested Operators</div>
              <div className="flex flex-wrap gap-1">
                {pattern.suggestedOperators.map((op, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs border border-[hsl(var(--border))] rounded">
                    {op}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 text-xs">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Clock className="h-3 w-3" />
              {new Date(pattern.discovered).toLocaleString()}
              <TrendingUp className="h-3 w-3 ml-2" />
              {(pattern.confidence * 100).toFixed(0)}% confidence
            </div>
            {!pattern.saved && (
              <button
                onClick={onSave}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-[hsl(var(--primary))] text-white rounded hover:opacity-90"
              >
                <Check className="h-3 w-3" />
                Save
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Target Card Component
function TargetCard({
  target,
  onCreate,
}: {
  target: DiscoveryTarget;
  onCreate: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <div className={cn(
        "rounded-lg border bg-[hsl(var(--card))] text-card-foreground shadow-sm",
        target.explored ? "border-green-500/30" : "border-[hsl(var(--border))]"
      )}>
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-lg font-semibold">
                {target.type === "api" && <Database className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                {target.type === "website" && <Globe className="h-4 w-4 text-green-500 flex-shrink-0" />}
                {target.type === "data_source" && <BookOpen className="h-4 w-4 text-purple-500 flex-shrink-0" />}
                {target.type === "service" && <Play className="h-4 w-4 text-yellow-500 flex-shrink-0" />}
                <span className="truncate">{target.name}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1 truncate">
                {target.description}
              </div>
            </div>
            {target.explored ? (
              <span className="px-2 py-1 text-xs rounded bg-green-500 text-white flex items-center gap-1">
                <Check className="h-3 w-3" />
                Operator Created
              </span>
            ) : target.confidence >= 0.7 ? (
              <span className="px-2 py-1 text-xs rounded bg-[hsl(var(--primary))] text-white">
                Eligible
              </span>
            ) : (
              <span className="px-2 py-1 text-xs rounded border border-[hsl(var(--border))]">
                {(target.confidence * 100).toFixed(0)}% confidence
              </span>
            )}
          </div>
        </div>

        <div className="p-6 pt-0 space-y-3">
          <div>
            <div className="text-xs font-medium text-muted-foreground mb-1">URL</div>
            <a
              href={target.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline text-sm flex items-center gap-1"
            >
              <span className="truncate">{target.url}</span>
              <ArrowRight className="h-3 w-3 flex-shrink-0" />
            </a>
          </div>

          {target.capabilities.length > 0 && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-2">Capabilities</div>
              <div className="flex flex-wrap gap-1">
                {target.capabilities.map((cap, i) => (
                  <span key={i} className="px-2 py-0.5 text-xs bg-[hsl(var(--muted))] rounded">
                    {cap}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!target.explored && target.confidence >= 0.7 && (
            <button
              onClick={onCreate}
              className="w-full flex items-center justify-center gap-1 px-3 py-1.5 text-sm bg-[hsl(var(--primary))] text-white rounded hover:opacity-90"
            >
              <Database className="h-3 w-3" />
              Create Operator
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
